import React, { useState, useEffect } from "react";
import {
  MdEdit,
  MdDeleteOutline,
  MdSearch,
  MdClose,
  MdAdd,
  MdCheckCircle,
  MdWarning,
  MdCategory
} from "react-icons/md";

const API_BASE = "https://api.visiontrackcrm.com";

function Categories() {
  const [name, setName] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit State
  const [editingCategory, setEditingCategory] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast / Alert
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/categories/all`);
      const data = await res.json();
      setCategories(data.data || []);
    } catch (error) {
      console.error(error);
      showToast("Failed to load categories", "error");
    }
  };

  const addCategory = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      showToast("Please enter a category name", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/categories/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() })
      });
      const data = await res.json();

      if (data.success) {
        showToast("Category added successfully!", "success");
        setName("");
        loadCategories();
      } else {
        showToast(data.message || "Failed to add category", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while adding category", "error");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (cat) => {
    setEditingCategory(cat);
    setEditName(cat.name);
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setEditingCategory(null);
    setEditName("");
  };

  // Save Edit
  const handleUpdateCategory = async (e) => {
    if (e) e.preventDefault();
    if (!editName.trim()) {
      showToast("Category name cannot be empty", "error");
      return;
    }

    try {
      setEditLoading(true);
      const res = await fetch(`${API_BASE}/categories/update/${editingCategory.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim() })
      });
      const data = await res.json();

      if (data.success) {
        showToast("Category updated successfully!", "success");
        closeEditModal();
        loadCategories();
      } else {
        showToast(data.message || "Failed to update category", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while updating category", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Open Delete Modal
  const openDeleteModal = (cat) => {
    setDeleteTarget(cat);
  };

  // Close Delete Modal
  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  // Confirm Delete
  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);
      const res = await fetch(`${API_BASE}/categories/delete/${deleteTarget.id}`, {
        method: "DELETE"
      });
      const data = await res.json();

      if (data.success) {
        showToast("Category deleted successfully!", "success");
        closeDeleteModal();
        loadCategories();
      } else {
        showToast(data.message || "Failed to delete category", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while deleting category", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filtered categories
  const filteredCategories = categories.filter((c) =>
    c.name ? c.name.toLowerCase().includes(searchTerm.toLowerCase()) : false
  );

  return (
    <div className="cat-page-container">
      <style>{`
        .cat-page-container {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          box-sizing: border-box;
        }

        /* HEADER */
        .cat-header {
          background: linear-gradient(135deg, #0067b1 0%, #1e40af 100%);
          padding: 26px 30px;
          border-radius: 20px;
          color: #ffffff;
          margin-bottom: 24px;
          box-shadow: 0 10px 24px rgba(0, 103, 177, 0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .cat-header-title {
          margin: 0;
          font-size: 1.6rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .cat-header-sub {
          margin: 6px 0 0 0;
          font-size: 0.9rem;
          color: #e0f2fe;
        }

        .cat-header-badge {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
          padding: 8px 16px;
          border-radius: 9999px;
          font-size: 0.85rem;
          font-weight: 700;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        /* TOAST */
        .cat-toast {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          animation: slideIn 0.3s ease;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .cat-toast.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .cat-toast.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* CARD FOR ADD CATEGORY */
        .cat-card {
          background: #ffffff;
          padding: 24px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
          margin-bottom: 24px;
        }

        .cat-card-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .cat-form-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .cat-input {
          height: 46px;
          padding: 0 16px;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.92rem;
          color: #1e293b;
          outline: none;
          transition: all 0.2s ease;
          flex: 1;
          min-width: 260px;
        }

        .cat-input:focus {
          border-color: #0067b1;
          box-shadow: 0 0 0 3px rgba(0, 103, 177, 0.12);
        }

        .cat-btn-add {
          height: 46px;
          padding: 0 24px;
          background: linear-gradient(135deg, #0067b1 0%, #1e40af 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-size: 0.92rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.2);
        }

        .cat-btn-add:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 103, 177, 0.3);
        }

        .cat-btn-add:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* SECTION CONTROLS (SEARCH & COUNT) */
        .cat-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
          margin-bottom: 18px;
        }

        .cat-section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .cat-search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 8px 14px;
          width: 260px;
        }

        .cat-search-box input {
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.88rem;
          color: #334155;
          width: 100%;
        }

        /* GRID */
        .cat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 18px;
        }

        /* CATEGORY CARD */
        .cat-item-card {
          background: #ffffff;
          padding: 18px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 14px;
        }

        .cat-item-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
          border-color: #cbd5e1;
        }

        .cat-item-top {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .cat-icon-badge {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: #eff6ff;
          color: #0067b1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 800;
          flex-shrink: 0;
        }

        .cat-info {
          flex: 1;
          overflow: hidden;
        }

        .cat-item-name {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cat-item-id {
          margin: 4px 0 0 0;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 500;
        }

        .cat-item-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          border-top: 1px solid #f1f5f9;
          padding-top: 12px;
        }

        .cat-btn-action {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }

        .cat-btn-edit {
          background: #f0f7ff;
          color: #0067b1;
          border-color: #bfdbfe;
        }

        .cat-btn-edit:hover {
          background: #0067b1;
          color: #ffffff;
        }

        .cat-btn-delete {
          background: #fff1f2;
          color: #e11d48;
          border-color: #fecdd3;
        }

        .cat-btn-delete:hover {
          background: #e11d48;
          color: #ffffff;
        }

        /* EMPTY STATE */
        .cat-empty {
          background: #ffffff;
          border-radius: 16px;
          padding: 48px 24px;
          text-align: center;
          color: #64748b;
          border: 1px dashed #cbd5e1;
        }

        /* MODAL STYLES */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-card {
          background: #ffffff;
          border-radius: 18px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          animation: modalPop 0.25s ease;
        }

        @keyframes modalPop {
          from {
            transform: scale(0.94);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border-radius: 8px;
        }

        .modal-close-btn:hover {
          color: #0f172a;
          background: #f1f5f9;
        }

        .modal-body {
          padding: 24px;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #f1f5f9;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-cancel {
          padding: 9px 18px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          color: #475569;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-cancel:hover {
          background: #f1f5f9;
        }

        .btn-save {
          padding: 9px 20px;
          background: #0067b1;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .btn-save:hover {
          background: #1e40af;
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-danger {
          padding: 9px 20px;
          background: #e11d48;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .btn-danger:hover {
          background: #be123c;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      {/* TOAST ALERT */}
      {toast && (
        <div className={`cat-toast ${toast.type}`}>
          {toast.type === "success" ? (
            <MdCheckCircle style={{ fontSize: "1.2rem" }} />
          ) : (
            <MdWarning style={{ fontSize: "1.2rem" }} />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* HEADER */}
      <div className="cat-header">
        <div>
          <h2 className="cat-header-title">
            <MdCategory /> Category Management
          </h2>
          <p className="cat-header-sub">
            Add, update, and manage optical product categories
          </p>
        </div>
        <div className="cat-header-badge">
          Total Categories: {categories.length}
        </div>
      </div>

      {/* ADD CATEGORY CARD */}
      <div className="cat-card">
        <h3 className="cat-card-title">
          <MdAdd style={{ color: "#0067b1", fontSize: "1.3rem" }} /> Add New Category
        </h3>
        <form onSubmit={addCategory} className="cat-form-row">
          <input
            className="cat-input"
            value={name}
            placeholder="Enter Category Name (e.g. Eyeglasses, Sunglasses)"
            onChange={(e) => setName(e.target.value)}
          />
          <button type="submit" className="cat-btn-add" disabled={loading}>
            <MdAdd style={{ fontSize: "1.2rem" }} />
            {loading ? "Adding..." : "Add Category"}
          </button>
        </form>
      </div>

      {/* SECTION HEADER & SEARCH */}
      <div className="cat-section-header">
        <h3 className="cat-section-title">All Categories ({filteredCategories.length})</h3>
        <div className="cat-search-box">
          <MdSearch style={{ color: "#94a3b8", fontSize: "1.2rem" }} />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#94a3b8",
                display: "flex",
                alignItems: "center"
              }}
            >
              <MdClose />
            </button>
          )}
        </div>
      </div>

      {/* CATEGORY GRID */}
      {filteredCategories.length === 0 ? (
        <div className="cat-empty">
          <MdCategory style={{ fontSize: "2.5rem", color: "#cbd5e1", marginBottom: "10px" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>
            {searchTerm ? "No categories match your search." : "No categories found. Add one above!"}
          </p>
        </div>
      ) : (
        <div className="cat-grid">
          {filteredCategories.map((item) => (
            <div key={item.id} className="cat-item-card">
              <div className="cat-item-top">
                <div className="cat-icon-badge">#</div>
                <div className="cat-info">
                  <h4 className="cat-item-name" title={item.name}>
                    {item.name}
                  </h4>
                  <p className="cat-item-id">ID: #{item.id}</p>
                </div>
              </div>

              <div className="cat-item-actions">
                <button
                  type="button"
                  className="cat-btn-action cat-btn-edit"
                  onClick={() => openEditModal(item)}
                >
                  <MdEdit style={{ fontSize: "1rem" }} />
                  Edit
                </button>
                <button
                  type="button"
                  className="cat-btn-action cat-btn-delete"
                  onClick={() => openDeleteModal(item)}
                >
                  <MdDeleteOutline style={{ fontSize: "1rem" }} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      {editingCategory && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <MdEdit style={{ color: "#0067b1" }} /> Edit Category
              </h3>
              <button className="modal-close-btn" onClick={closeEditModal}>
                <MdClose />
              </button>
            </div>

            <form onSubmit={handleUpdateCategory}>
              <div className="modal-body">
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#475569",
                    marginBottom: "8px"
                  }}
                >
                  Category Name
                </label>
                <input
                  className="cat-input"
                  style={{ width: "100%", boxSizing: "border-box" }}
                  value={editName}
                  autoFocus
                  placeholder="Enter category name"
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closeEditModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={editLoading}>
                  {editLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: "#e11d48" }}>
                <MdWarning style={{ color: "#e11d48" }} /> Confirm Delete
              </h3>
              <button className="modal-close-btn" onClick={closeDeleteModal}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body">
              <p style={{ margin: "0 0 12px 0", color: "#334155", fontSize: "0.95rem" }}>
                Are you sure you want to delete category{" "}
                <strong style={{ color: "#0f172a" }}>"{deleteTarget.name}"</strong> (ID: #{deleteTarget.id})?
              </p>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>
                This action cannot be undone. Products associated with this category may be affected.
              </p>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteCategory}
                disabled={deleteLoading}
              >
                <MdDeleteOutline style={{ fontSize: "1.1rem" }} />
                {deleteLoading ? "Deleting..." : "Delete Category"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Categories;